<template>
  <div>
    <figure class="highcharts-figure">
      <highcharts ref="chart" :options="options" />
      <p class="highcharts-description highcharts-linked-description">
        <strong>Figure : </strong> {{ $t('compGraphicsRActivitiesPie.help') }}
      </p>
    </figure>
    <br v-if="!onlyGraphics" />
    <b-table
      v-if="!onlyGraphics"
      :striped="true"
      :narrowed="true"
      :hoverable="true"
      :data="series.series"
    >
      <b-table-column
        :label="$t('compGraphicsRActivitiesPie.label-1')"
        v-slot="props"
      >
        {{ props.row.name }}
      </b-table-column>
      <b-table-column
        :label="$t('emissions.t.co2e.short')"
        numeric
        v-slot="props"
      >
        {{ Math.round(props.row.y) | splitNumberTons }} &plusmn;
        {{ Math.round(props.row.uncertainty) | splitNumberTons }}
      </b-table-column>
      <b-table-column
        :label="$t('compGraphicsRActivitiesPie.label-2')"
        numeric
        v-slot="props"
      >
        {{
          Math.round(
            (props.row.y / ghgi.emissions.ractivities.intensity.intensity) * 100
          )
        }}
        %
      </b-table-column>
    </b-table>
  </div>
</template>

<script>
import { researchActivityClass } from '@/models/carbon/ResearchActivity.js'
import Highcharts from 'highcharts'
import '@/plugins/highcharts.js'

export default {
  name: 'RActivitiesPie',
  props: {
    ghgi: {
      type: Object,
      default: null
    },
    onlyGraphics: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    series: function () {
      let colors = Highcharts.getOptions().colors
      let series = {
        series: [],
        drilldown: []
      }
      for (let ractivity of this.ghgi.ractivities) {
        let oindex = series.series.findIndex(
          (obj) => obj.name === this.$t(ractivity.type)
        )
        let dindex = series.drilldown.findIndex(
          (obj) => obj.name === this.getDescription(ractivity)
        )
        if (oindex < 0) {
          series.series.push({
            name: this.$t(ractivity.type),
            y: ractivity.intensity.intensity,
            color: colors[series.series.length],
            uncertainty: ractivity.intensity.uncertainty
          })
        } else {
          series.series[oindex].y += ractivity.intensity.intensity
          series.series[oindex].uncertainty += ractivity.intensity.uncertainty
        }
        oindex = series.series.findIndex(
          (obj) => obj.name === this.$t(ractivity.type)
        )
        if (dindex < 0) {
          series.drilldown.push({
            name: this.getDescription(ractivity),
            y: ractivity.intensity.intensity,
            color: Highcharts.color(series.series[oindex].color)
              .brighten(0.12)
              .get()
          })
        } else {
          series.drilldown[dindex].y += ractivity.intensity.intensity
        }
      }
      return series
    },
    options: function () {
      const self = this
      return {
        chart: {
          type: 'pie'
        },
        title: {
          verticalAlign: 'middle',
          floating: true,
          text: `<b>${self.$options.filters.splitNumberTons(
            Math.round(self.ghgi.emissions.ractivities.intensity.intensity)
          )} ${self.$t('t.co2e')} </b>`
        },
        plotOptions: {
          pie: {
            innerSize: '60%',
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              formatter () {
                return `<b>${
                  this.point.name
                }:</b> ${self.$options.filters.splitNumberTons(
                  this.y
                )} ${self.$t('t.co2e')}`
              }
            },
            showInLegend: true
          }
        },
        series: [
          {
            name: this.$t('carbon.footprint'),
            colorByPoint: true,
            data: this.series.series,
            size: '70%',
            dataLabels: {
              formatter: function () {
                return this.point.name
              },
              color: '#ffffff',
              distance: -30
            }
          },
          {
            name: '',
            data: this.series.drilldown,
            size: '100%',
            innerSize: '70%'
          }
        ],
        legend: {
          enabled: false
        },
        tooltip: {
          formatter: function () {
            return `${this.key}<br/><b>${self.$options.filters.splitNumberTons(
              this.y
            )} ${self.$t('t.co2e')}</b>`
          }
        }
      }
    }
  },
  methods: {
    getDescription: function (ractivity) {
      let rclass = researchActivityClass(ractivity.type)
      return rclass.getDescription(
        ractivity.category,
        ractivity.type,
        ractivity.subtype,
        this.$i18n.locale
      )
    }
  }
}
</script>

<style scoped>
.highcharts-figure {
  min-width: 320px;
  max-width: 700px;
  margin: 1em auto;
}
.highcharts-description {
  text-align: center;
  color: '#777777';
  font-style: italic;
}
</style>
